import React, { useState } from 'react';
import { Input } from 'antd';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import Modal from '../../../components/Modal';
import { useUpdateTitle } from '../../../core/modules/book/hooks/useUpdateTitle.query';
import {
  PENDING_EXCLUSION,
  PENDING_EXCLUSION_AO3,
  PENDING_EXCLUSION_RR,
  SEND_TO_CRM, SENT_TO_CRM_AO3,
  SENT_TO_CRM_RR,
  SOURCE
} from '../../../core/constants';
import Endpoints from '../../../core/constants/endpoints';
import { IBook } from '../../../section/Common/types/types';
import { useActions } from '../../../context/Actions.context';
import { useServices } from '../../../core/services/useServices';
import showActionNotification from '../../../section/Common/utils/showActionNotification';

const { TextArea } = Input;

interface IProps {
  book: IBook | Record<string, string | number>;
  modal: boolean;
  toggleModal: Function;
  id: string;
}

const createExclusionObject = (source: string, book: IBook | Record<string, string | number>) => {
  if (source === SOURCE.bookstat) {
    return { amazonasin: book.ASIN };
  } else if (source === SOURCE.ao3) {
    return { work_id: book.work_id };
  }
  else {
    return { fiction_id: book['Fiction Id'] };
  }
};

const getExclusionType = (source: string) => {
  if (source === SOURCE.bookstat) {
    return PENDING_EXCLUSION;
  } else if (source === SOURCE.ao3) {
    return PENDING_EXCLUSION_AO3;
  }
  else {
    return PENDING_EXCLUSION_RR;
  }
};

const PendingExclusionTitles: React.FC<IProps> = ({ book, modal, toggleModal, id }) => {
  const { source, isActionLoading, setActionLoading } = useActions();
  const { book: bookService } = useServices();
  const queryClient = useQueryClient();
  const history = useHistory();
  const pendingExclusion = createExclusionObject(source['source'], book);
  const exclusionType = getExclusionType(source['source']);
  const [notes, setNotes] = useState<string>('');

  const { mutateAsync: mutateExclusion } = useUpdateTitle(exclusionType);

  const handleClose = () => {
    setNotes('');
    toggleModal();
  };

  const onSend = () => {
    setActionLoading(true);

    const body = {
      ...(notes.trim() && { notes: notes.trim() })
    };

    const capturedSource = source['source'];
    const capturedParams = { ...pendingExclusion };

    const promise = mutateExclusion({ params: pendingExclusion, body });

    promise.then(res => {
      if (res?.status === 200) {
        showActionNotification({
          type: 'success',
          message: 'The Title was successfully sent to the Pending Exclusion tab.',
          destination: {
            name: 'Pending Exclusion',
            onGoTo: () => history.push(`${capturedSource}/pending-exclusion`)
          },
          onUndo: async () => {
            await bookService.updateTitle(
              {
                source: capturedSource,
                sourceTab: 'pending-exclusion',
                endpoint: Endpoints[id],
                action: id
              },
              capturedParams
            );
          },
          queryClient,
          id
        });
      } else {
        showActionNotification({
          type: 'error',
          message: 'The Title has not been sent to the Pending Exclusion tab.'
        });
      }
    }).catch(() => {
      showActionNotification({
        type: 'error',
        message: 'The Title has not been sent to the Pending Exclusion tab.'
      });
    });

    promise.then(() => {
      if (id === SEND_TO_CRM || id === SENT_TO_CRM_RR || id === SENT_TO_CRM_AO3) {
        showActionNotification({ type: 'info', message: 'Please delete the data from Hubspot.' });
      }
    });
    promise.then(() => queryClient.refetchQueries(`get-row-count${id}`));
    promise.then(() => queryClient.refetchQueries(`get-${id}`));
    promise.finally(() => setActionLoading(false));

    handleClose();
  };

  return (
    <Modal
      okText={'Send To Pending Exclusion'}
      title='Send To Pending Exclusion'
      visible={modal}
      onOk={onSend}
      confirmLoading={isActionLoading}
      onCancel={handleClose}
    >
      <div>
        <span>
          Help yourself and others understand why you&apos;re sending this. Leave a note, or skip and
          continue.
        </span>
        <br />
        <br />
        <TextArea
          placeholder='Add your notes here...'
          maxLength={400}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
        />

        <div style={{ textAlign: 'right', fontSize: 12, marginTop: 4 }}>
          {notes.length} / 400
        </div>
      </div>
    </Modal>
  );
};

export default React.memo(PendingExclusionTitles);